import { useTranslation } from 'react-i18next';
import { useToast } from '@/shared/hooks';
import { ViewTypePopover } from '@/shared/components/custom/view-type-popover';
import { BOARD_VIEW_TYPE } from '@/features/tasks/public';
import { useCreateBoardView } from '../hooks/useBoardViewMutations';

const ALL_VIEW_TYPES = [
  BOARD_VIEW_TYPE.List,
  BOARD_VIEW_TYPE.Kanban,
  BOARD_VIEW_TYPE.Calendar,
  BOARD_VIEW_TYPE.Gantt,
  BOARD_VIEW_TYPE.Documents,
  BOARD_VIEW_TYPE.Dashboard,
  BOARD_VIEW_TYPE.Activity,
  BOARD_VIEW_TYPE.Timeline,
  BOARD_VIEW_TYPE.Whiteboard,
] as const;

const VIEW_TYPE_KEY: Record<number, string> = {
  [BOARD_VIEW_TYPE.List]: 'list',
  [BOARD_VIEW_TYPE.Kanban]: 'kanban',
  [BOARD_VIEW_TYPE.Calendar]: 'calendar',
  [BOARD_VIEW_TYPE.Gantt]: 'gantt',
  [BOARD_VIEW_TYPE.Documents]: 'documents',
  [BOARD_VIEW_TYPE.Dashboard]: 'dashboard',
  [BOARD_VIEW_TYPE.Activity]: 'activity',
  [BOARD_VIEW_TYPE.Timeline]: 'timeline',
  [BOARD_VIEW_TYPE.Whiteboard]: 'whiteboard',
};

interface AddViewPopoverProps {
  boardId: string;
  existingTypes: number[];
}

export function AddViewPopover({ boardId, existingTypes }: AddViewPopoverProps) {
  const { t } = useTranslation('boards');
  const { toast } = useToast();
  const createView = useCreateBoardView(boardId);

  const options = ALL_VIEW_TYPES.map((type) => ({
    type,
    label: t(`views.types.${VIEW_TYPE_KEY[type]}`),
  }));

  async function handleAdd(type: number) {
    const name = t(`views.types.${VIEW_TYPE_KEY[type]}`);
    try {
      await createView.mutateAsync({ type, name });
      toast.success({ title: t('views.toast.createSuccess') });
    } catch {
      // error handled by global handler
    }
  }

  return (
    <ViewTypePopover
      options={options}
      existingTypes={existingTypes}
      onAdd={handleAdd}
      isPending={createView.isPending}
      addLabel={t('views.addView')}
      testIdPrefix="board-views"
    />
  );
}
